import { parseRetryAfterMs } from '@/shared/lib/retryAfter';

type ApiResponseType = 'json' | 'text' | 'void' | 'blob';

type ApiSearchParamScalar = string | number | boolean;
type ApiSearchParamValue =
  | ApiSearchParamScalar
  | readonly ApiSearchParamScalar[]
  | null
  | undefined;

type ApiSearchParams<TSearch extends object> = TSearch & {
  readonly [Key in keyof TSearch]: TSearch[Key] extends ApiSearchParamValue ? TSearch[Key] : never;
};

export type ApiAuthFailureReason = 'missing-session' | 'token-failed';

export class ApiAuthError extends Error {
  readonly kind = 'auth' as const;
  readonly reason: ApiAuthFailureReason;

  constructor(reason: ApiAuthFailureReason, cause?: unknown) {
    super(
      reason === 'missing-session' ? 'Authentication is required' : 'Could not obtain a token',
      {
        cause,
      },
    );
    this.name = 'ApiAuthError';
    this.reason = reason;
  }
}

export class ApiNetworkError extends Error {
  readonly kind = 'network' as const;

  constructor(cause?: unknown) {
    super('The API request did not receive a response', { cause });
    this.name = 'ApiNetworkError';
  }
}

export class ApiHttpError extends Error {
  readonly kind = 'http' as const;
  readonly status: number;
  readonly retryAfterMs?: number;
  /** Parsed non-success response body. Feature code must validate route-specific shapes. */
  readonly payload?: unknown;
  /** Server-provided error/reason text. Display it only when the endpoint guarantees public copy. */
  readonly serverMessage: string;

  constructor(status: number, serverMessage: string, retryAfterMs?: number, payload?: unknown) {
    super(serverMessage);
    this.name = 'ApiHttpError';
    this.status = status;
    this.serverMessage = serverMessage;
    this.retryAfterMs = retryAfterMs;
    this.payload = payload;
  }
}

export function isUnauthorized(error: unknown): boolean {
  return (
    (error instanceof ApiAuthError && error.reason === 'missing-session') ||
    (error instanceof ApiHttpError && error.status === 401)
  );
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiHttpError && error.status === 403;
}

export function isRetryableApiError(error: unknown): boolean {
  return (
    error instanceof ApiNetworkError ||
    (error instanceof ApiHttpError &&
      (error.status === 408 || error.status === 429 || error.status >= 500))
  );
}

/**
 * The server could not determine whether an idempotent write committed. The
 * caller must retry the original request key rather than creating a new one.
 */
export function isCommitOutcomeUnknown(error: unknown): boolean {
  if (!(error instanceof ApiHttpError) || (error.status !== 409 && error.status !== 503))
    return false;
  if (!error.payload || typeof error.payload !== 'object') return false;
  const payload = error.payload as Record<string, unknown>;
  return (
    payload.retryable === true &&
    (payload.code === 'commit_outcome_unknown' || payload.error === 'commit_outcome_unknown')
  );
}

interface ApiClientConfig {
  basePath: string;
  getAccessToken?: () => Promise<string | null>;
  onUnauthorized?: (error: ApiAuthError | ApiHttpError) => void;
}

interface ApiRequestBase<TSearch extends object = Record<never, never>> {
  cache?: RequestCache;
  headers?: HeadersInit;
  responseType?: ApiResponseType;
  searchParams?: ApiSearchParams<TSearch>;
  signal?: AbortSignal;
}

type ApiRequestBody = { body?: never; json?: unknown } | { body?: BodyInit | null; json?: never };

export type ApiReadOptions<TSearch extends object = Record<never, never>> = ApiRequestBase<TSearch>;
export type ApiWriteOptions<TSearch extends object = Record<never, never>> =
  ApiRequestBase<TSearch> & ApiRequestBody;
export type ApiCallOptions = Pick<ApiReadOptions, 'signal'>;

function serializeSearchParams(searchParams: object | undefined): string {
  if (!searchParams) return '';

  const serialized = new URLSearchParams();
  const append = (key: string, value: ApiSearchParamScalar) => {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      throw new TypeError(`Search parameter "${key}" must be a finite number`);
    }
    serialized.append(key, String(value));
  };
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item !== 'string' && typeof item !== 'number' && typeof item !== 'boolean') {
          throw new TypeError(
            `Search parameter "${key}" entries must be strings, numbers, or booleans`,
          );
        }
        append(key, item);
      }
      continue;
    }
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      throw new TypeError(`Search parameter "${key}" must be a string, number, or boolean`);
    }
    append(key, value);
  }
  return serialized.toString();
}

function requestUrl(basePath: string, path: string, searchParams: object | undefined): string {
  if (path.includes('?')) {
    throw new TypeError(
      'ApiClient paths must not contain a query string; use searchParams instead',
    );
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const query = serializeSearchParams(searchParams);
  return `${basePath}${normalizedPath}${query ? `?${query}` : ''}`;
}

function extractServerMessage(body: unknown, response: Response): string {
  if (body && typeof body === 'object') {
    const value = body as Record<string, unknown>;
    if (response.status === 403 && value.decision === 'deny' && typeof value.reason === 'string') {
      return value.reason;
    }
    if (typeof value.error === 'string') return value.error;
    if (typeof value.reason === 'string') return value.reason;
  }
  return response.statusText || `Request failed with status ${response.status}`;
}

function parseBody(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isAbortError(error: unknown): boolean {
  return (
    error !== null && typeof error === 'object' && 'name' in error && error.name === 'AbortError'
  );
}

async function readResponse<T>(response: Response, responseType: ApiResponseType): Promise<T> {
  if (response.ok && responseType === 'blob') {
    try {
      return (await response.blob()) as T;
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new ApiNetworkError(error);
    }
  }
  let text: string;
  try {
    text = await response.text();
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new ApiNetworkError(error);
  }
  const parsedBody = parseBody(text);

  if (!response.ok) {
    throw new ApiHttpError(
      response.status,
      extractServerMessage(parsedBody, response),
      parseRetryAfterMs(response.headers.get('Retry-After')),
      parsedBody,
    );
  }

  if (responseType === 'void') return undefined as T;
  if (responseType === 'text') return text as T;
  return parsedBody as T;
}

/**
 * Native-fetch API transport. It owns authentication, serialization, response decoding,
 * cancellation, and transport errors. Endpoint paths and Zod contracts stay in feature `api/`.
 */
export class ApiClient {
  private readonly basePath: string;
  private readonly getAccessToken?: () => Promise<string | null>;
  private readonly onUnauthorized?: (error: ApiAuthError | ApiHttpError) => void;

  constructor({ basePath, getAccessToken, onUnauthorized }: ApiClientConfig) {
    this.basePath = basePath.replace(/\/$/, '');
    this.getAccessToken = getAccessToken;
    this.onUnauthorized = onUnauthorized;
  }

  get<T = unknown, TSearch extends object = Record<never, never>>(
    path: string,
    options: ApiReadOptions<TSearch> = {},
  ): Promise<T> {
    return this.request<T, TSearch>('GET', path, options);
  }

  post<T = unknown, TSearch extends object = Record<never, never>>(
    path: string,
    options: ApiWriteOptions<TSearch> = {},
  ): Promise<T> {
    return this.request<T, TSearch>('POST', path, options);
  }

  put<T = unknown, TSearch extends object = Record<never, never>>(
    path: string,
    options: ApiWriteOptions<TSearch> = {},
  ): Promise<T> {
    return this.request<T, TSearch>('PUT', path, options);
  }

  patch<T = unknown, TSearch extends object = Record<never, never>>(
    path: string,
    options: ApiWriteOptions<TSearch> = {},
  ): Promise<T> {
    return this.request<T, TSearch>('PATCH', path, options);
  }

  delete<T = unknown, TSearch extends object = Record<never, never>>(
    path: string,
    options: ApiWriteOptions<TSearch> = {},
  ): Promise<T> {
    return this.request<T, TSearch>('DELETE', path, options);
  }

  private async request<T, TSearch extends object>(
    method: string,
    path: string,
    options: ApiReadOptions<TSearch> | ApiWriteOptions<TSearch>,
  ): Promise<T> {
    const url = requestUrl(this.basePath, path, options.searchParams);
    const headers = new Headers(options.headers);
    headers.set('Accept', headers.get('Accept') ?? 'application/json');

    if (this.getAccessToken) {
      let token: string | null;
      try {
        token = await this.getAccessToken();
      } catch (error) {
        throw new ApiAuthError('token-failed', error);
      }
      if (!token) {
        const error = new ApiAuthError('missing-session');
        this.notifyUnauthorized(error);
        throw error;
      }
      headers.set('Authorization', `Bearer ${token}`);
    }

    let body: BodyInit | null | undefined;
    if ('json' in options) {
      body = JSON.stringify(options.json);
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    } else if ('body' in options) {
      body = options.body;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body,
        signal: options.signal,
        cache: options.cache,
        // API authentication is Bearer-only; unrelated browser cookies must not reach the proxy.
        credentials: 'omit',
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new ApiNetworkError(error);
    }

    try {
      return await readResponse<T>(response, options.responseType ?? 'json');
    } catch (error) {
      if (error instanceof ApiHttpError && error.status === 401) {
        this.notifyUnauthorized(error);
      }
      throw error;
    }
  }

  private notifyUnauthorized(error: ApiAuthError | ApiHttpError): void {
    try {
      this.onUnauthorized?.(error);
    } catch {
      // Session-expiry presentation must never replace the transport error.
    }
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
